
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ExtendedClinic } from "@/types/database";

// Demo mode flag - should match the flag in useAuth.tsx
const DEMO_MODE = true;

// Mock demo clinic data
const demoClinic: ExtendedClinic = {
  id: 'demo-clinic-123',
  name: 'Demo Healthcare Clinic',
  address: '123 Demo Street',
  city: 'Demo City',
  state: 'Demo State',
  zip_code: '12345',
  phone: '+1-555-0123',
  email: 'contact@demohealthcare.com',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  active: true,
  onboarding_status: 'completed',
  subscription_plan: 'premium'
};

export function useClinicData(clinicId?: string) {
  return useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: async (): Promise<ExtendedClinic | null> => {
      if (!clinicId) return null;
      
      console.log('Fetching clinic data for:', clinicId);

      // Return mock data in demo mode
      if (DEMO_MODE) {
        console.log('Demo mode - returning mock clinic data');
        return clinicId === 'demo-clinic-123' ? demoClinic : null;
      }
      
      const { data, error } = await supabase
        .from('clinics')
        .select('*')
        .eq('id', clinicId)
        .maybeSingle();

      if (error) {
        // Only throw if it's not a "no rows returned" error
        if (error.code !== 'PGRST116') {
          console.error('Error fetching clinic data:', error);
          throw error;
        }
        console.warn('No clinic found with ID:', clinicId);
        return null;
      }
      
      if (!data) {
        console.warn('No clinic found with ID:', clinicId);
        return null;
      }

      // Map the basic clinic data to extended clinic structure
      const extendedClinic: ExtendedClinic = {
        ...data,
        onboarding_status: 'pending', // Default value since column might not exist yet
        subscription_plan: 'free' // Default value since column might not exist yet
      };

      return extendedClinic;
    },
    enabled: !!clinicId,
  });
}

export function useClinics() {
  return useQuery({
    queryKey: ['clinics'],
    queryFn: async (): Promise<ExtendedClinic[]> => {
      console.log('Fetching all clinics');

      // Return mock data in demo mode
      if (DEMO_MODE) {
        console.log('Demo mode - returning mock clinics data');
        return [demoClinic];
      }
      
      const { data, error } = await supabase
        .from('clinics')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) {
        console.error('Error fetching clinics:', error);
        throw error;
      }

      // Map the basic clinic data to extended clinic structure
      const extendedClinics: ExtendedClinic[] = (data || []).map(clinic => ({
        ...clinic,
        onboarding_status: 'pending', // Default value since column might not exist yet
        subscription_plan: 'free' // Default value since column might not exist yet
      }));

      return extendedClinics;
    },
  });
}
